import { gibtEs, legeOrdnerAn } from './dateien';

/**
 * Einen Ordner unter `Documents` sicherstellen, ohne die Konsole zu fluten.
 *
 * ⚠️ **`mkdir` ist auch mit `recursive: true` nicht idempotent.** Besteht der Ordner schon,
 * scheitert der Aufruf mit `OS-PLUG-FILE-0010` („already exists, cannot be overwritten"). Ein
 * `try/catch` im TypeScript hilft dagegen **nicht**: die Capacitor-Brücke hat den abgelehnten
 * Plugin-Aufruf da längst nach nativ gemeldet und in die Xcode-Konsole geschrieben. Am
 * 2026-09-12 am Gerät gesehen — bei jedem einzelnen Logeintrag eine Fehlerzeile, dazu bei
 * jedem Bildschirmabzug, Foto und Video. Echte Fehler gingen darin unter.
 *
 * Deshalb erst fragen, ob es ihn gibt: auf einen bestehenden Ordner gelingt das und meldet
 * nichts. Angelegt wird nur, wenn wirklich etwas fehlt.
 */

/**
 * Welche Ordner in dieser Sitzung schon sichergestellt wurden.
 *
 * Damit kostet auch die Frage danach nur einmal. Der Merker gilt bewusst **nur** für diese Sitzung
 * und nur für Erfolge — ein Ordner, dessen Anlegen scheiterte, wird beim nächsten Mal wieder
 * versucht.
 */
const bekannt = new Set<string>();

/**
 * Welche Ordner in dieser Sitzung nachweislich **fehlen**.
 *
 * Gegenstück zu `bekannt`, gefüllt von den Lesern (`storage.ts::listFiles`): wer einmal
 * „gibt es nicht" gehört hat, braucht nicht bei jedem Bildaufbau erneut zu fragen. Am
 * 2026-09-21 am Gerät gesehen — dieselben vier Ordner in einer Endlosschleife, tausende
 * Zeilen `OS-PLUG-FILE-0008`, und der eine Fehler, den wir suchten, ging darin unter.
 *
 * ⚠️ **Nur diese Sitzung, und jedes Anlegen hebt den Merker auf.** Sonst bliebe ein Ordner
 * für den Rest des Laufs als leer gemeldet, obwohl gerade eine Aufnahme hineingeschrieben
 * wurde.
 */
const fehlend = new Set<string>();

/**
 * Wohin ein gescheitertes Anlegen gemeldet wird.
 *
 * ⚠️ **Hineingereicht, nicht importiert** (angemeldet in `main.tsx`, dieselbe Bauform wie
 * `fotos.ts::setBeschriftungsQuelle`). `appLog.ts` ruft selbst `stelleOrdnerSicher` für
 * `logs/app` — ein Import hier wäre ein Kreis.
 */
export type Ordnermelder = (text: string) => void | Promise<void>;

let melder: Ordnermelder | null = null;

export function setzeOrdnerMelder(fn: Ordnermelder | null): void {
  melder = fn;
}

/**
 * Läuft gerade eine Meldung?
 *
 * ⚠️ **Ohne diese Sperre stünde hier eine Endlosschleife.** Der Melder ist das Dateilog, und
 * das Dateilog stellt seinerseits `logs/app` sicher. Scheitert genau dieses Anlegen, riefe die
 * Meldung das Log, das Log den Ordner, der Ordner die Meldung — bis der Stapel reisst.
 */
let meldetGerade = false;

function melde(text: string): void {
  if (melder === null || meldetGerade) return;
  meldetGerade = true;
  try {
    void Promise.resolve(melder(text)).catch(() => undefined).finally(() => {
      meldetGerade = false;
    });
  } catch {
    meldetGerade = false;
  }
}

export async function stelleOrdnerSicher(pfad: string): Promise<void> {
  if (bekannt.has(pfad)) return;

  if (await gibtEs(pfad)) {
    merkeVorhanden(pfad);
    return;
  }

  try {
    await legeOrdnerAn(pfad);
    merkeVorhanden(pfad);
  } catch (fehler) {
    // Kein Platz, kein Zugriff, oder ein Wettlauf mit einem zweiten Aufruf. Nicht gemerkt,
    // nicht geworfen: der Aufrufer scheitert gleich beim Schreiben und behandelt es dort.
    //
    // ⚠️ **Aber nicht mehr stumm.** Bis zum 2026-09-21 stand hier ein leeres `catch`; ein
    // Ordner, der nicht entstand, liess jede Aufnahme scheitern, und im Dateilog stand nichts
    // darüber — nur die Folgefehler der Aufrufer, die wie eigenständige Fehler aussahen.
    melde('Ordner ' + pfad + ' liess sich nicht anlegen: ' + String(fehler));
  }
}

/** Ein Ordner ist da: merken und einen etwaigen Fehlt-Vermerk aufheben. */
function merkeVorhanden(pfad: string): void {
  bekannt.add(pfad);
  fehlend.delete(pfad);
}

/**
 * Wissen wir sicher, dass dieser Ordner fehlt?
 *
 * Für Leser, die auf einen fehlenden Ordner mit einer leeren Liste antworten — sie sparen
 * sich damit den Plugin-Aufruf und die Fehlerzeile in der Konsole.
 */
export function ordnerFehlt(pfad: string): boolean {
  return fehlend.has(pfad);
}

/**
 * Einen Ordner als fehlend vermerken — gerufen von Lesern, denen das Plugin „does not exist"
 * gemeldet hat.
 *
 * Ein bereits als vorhanden bekannter Ordner wird **nicht** übergangen: dann irrt der Leser,
 * nicht der Merker.
 */
export function merkeOrdnerFehlt(pfad: string): void {
  if (!bekannt.has(pfad)) fehlend.add(pfad);
}

/** Nur für Tests: den Merker leeren, damit jeder Fall von vorn beginnt. */
export function vergissOrdner(): void {
  bekannt.clear();
  fehlend.clear();
}
